import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import StudioLayerAnimations from "./StudioLayerAnimations";
import StudioArtboardTransitions from "./StudioArtboardTransitions";
import StudioAnimationPlayer from "./StudioAnimationPlayer";
import StudioPagePresets from "./StudioPagePresets";
import { StoryTypes } from "../../../../_utils/constants";
import { ReactComponent as IconMotion } from "../../../../assets/icons/motion.svg";
import { grey, lightInactive, primary, white } from "../../../styled/variable";
import { ENTER_ANIMATION, EXIT_ANIMATION } from "../../../../_actions/types";
import { FaSignInAlt, FaSignOutAlt } from "react-icons/fa";
import {
  TabList,
  Tabs,
  Tab,
  HStack,
  Text,
  Button,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { wsConvertStory } from "../../../../_actions/webSocketAction";

const StyledGetStartedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  .icon {
    width: 40px;
    height: 40px;
    fill: ${white};
    color: ${white};
    margin-bottom: 16px;
  }

  .title-text {
    font-size: 20px;
    font-weight: 500;
    color: ${lightInactive};

    & > span {
      color: ${primary};
    }

    margin-bottom: 4px;
  }

  .description {
    font-size: 16px;
    color: ${grey};
    text-align: center;
    margin-bottom: 16px;
  }

  button {
    align-self: stretch;
  }
`;

const animationTabs = [
  {
    icon: FaSignInAlt,
    name: ENTER_ANIMATION,
  },
  {
    icon: FaSignOutAlt,
    name: EXIT_ANIMATION,
  },
];

export const StudioAnimations = (props) => {
  const { canvasRef } = props;
  const [currentTab, setCurrentTab] = useState("enter");

  const { t } = useTranslation(["common"]);

  const editor = useSelector((state) => state.editor);
  const pages = useSelector((state) => state.pagestore.pages);
  const dispatch = useDispatch((state) => state.pagestore.pages);
  const convertStoryAction = useCallback(
    (storyType) => {
      dispatch(wsConvertStory(storyType));
    },
    [dispatch]
  );

  const currentPageAnimation = pages[editor.activePage.id]
    ? pages[editor.activePage.id].payload.animation
    : {};

  const addAnimation = () => {
    convertStoryAction(StoryTypes.ANIMATED);
  };

  const onSelectedTabChange = (selectedTabIndex) => {
    if (selectedTabIndex === 0) {
      setCurrentTab(ENTER_ANIMATION);
    } else if (selectedTabIndex === 1) {
      setCurrentTab(EXIT_ANIMATION);
    }
  };

  return (
    <>
      {currentPageAnimation ? (
        <>
          <StudioAnimationPlayer
            currentTab={currentTab}
            canvasRef={canvasRef}
          />
          {editor.activeElement.id ? (
            <>
              <Tabs
                id="studio-animation-sidebar-tab"
                defaultIndex={0}
                onChange={onSelectedTabChange}
                isFitted
                isLazy
              >
                <TabList>
                  {animationTabs.map((tab, index) => {
                    let Icon = tab?.icon;
                    return (
                      <Tab key={"studio_animation_tab_" + index}>
                        <HStack>
                          <Icon className="fa-16" />
                          <Text textTransform={"capitalize"} fontSize={"sm"}>
                            {tab.name}
                          </Text>
                        </HStack>
                      </Tab>
                    );
                  })}
                </TabList>
                <TabPanels>
                  {animationTabs.map((tab, index) => {
                    return (
                      <TabPanel key={"studio_animation_tab_panel_" + index}>
                        <StudioLayerAnimations
                          selectedTab={currentTab}
                          canvasRef={canvasRef}
                        ></StudioLayerAnimations>
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </>
          ) : (
            <>
              <StudioPagePresets canvasRef={canvasRef}></StudioPagePresets>
              <StudioArtboardTransitions
                canvasRef={canvasRef}
              ></StudioArtboardTransitions>
            </>
          )}
        </>
      ) : (
        <StyledGetStartedContainer>
          <IconMotion className="icon" />
          <div className="title-text">
            {t("animation")} <span className="highlight">Simplified</span>
          </div>
          <div className="description">{t("animation_intro_heading")}</div>
          <Button variant="solid" onClick={addAnimation}>
            {t("get_started")}
          </Button>
        </StyledGetStartedContainer>
      )}
    </>
  );
};

StudioAnimations.propTypes = {};

export default StudioAnimations;
